import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  IconButton,
  Checkbox,
  FormControlLabel,
  Tooltip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import InfoIcon from '@mui/icons-material/Info';
import DownloadIcon from '@mui/icons-material/Download';
import { formatPhoneNumber } from '../utils/utils';
import IELogo from '../assets/IE_Logo.png';
import TPCLogo from '../assets/TPC_Logo.png';
import { toZonedTime, format } from 'date-fns-tz';
import { supabase } from '../supabaseClient';

// Define admin users
const ADMIN_USERS = [
  'adam.hatch@ivanhoeelectric.com',
  'angelica.folk@ivanhoeelectric.com',
  'nate@tipple.consulting'
];

const SiteContacts = ({ site, user, timezone }) => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [currentContact, setCurrentContact] = useState(null);
  const [originalContact, setOriginalContact] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [openConfirmDelete, setOpenConfirmDelete] = useState(false);
  const [openConfirmClose, setOpenConfirmClose] = useState(false);
  const requestIdRef = useRef(0);

  // Check if current user is admin
  const isAdmin = user?.email && ADMIN_USERS.includes(user.email.toLowerCase());

  // Check if user can edit a specific contact
  const canEditContact = (contact) => {
    if (isAdmin) return true; // Admins can edit any contact
    if (!user?.email) return false; // No user logged in
    return String(contact.email || '').toLowerCase() === user.email.toLowerCase(); // Users can only edit their own contact
  };

  const fetchContacts = useCallback(async () => {
    if (!site || site === "undefined") {
      setError("Invalid site parameter");
      setLoading(false);
      return;
    }

    requestIdRef.current += 1;
    const requestId = requestIdRef.current;

    setLoading(true);
    setError(null);

    try {
      const { data, error } = await supabase
        .from('site_contacts')
        .select('*')
        .eq('site', site)
        .order('last_name', { ascending: true })
        .order('first_name', { ascending: true });

      if (error) {
        throw error;
      }

      const result = Array.isArray(data) ? data : [];
      const sortedContacts = result.sort((a, b) => {
        const lastNameCompare = (a.last_name || '').localeCompare(b.last_name || '');
        if (lastNameCompare !== 0) {
          return lastNameCompare;
        }
        return (a.first_name || '').localeCompare(b.first_name || '');
      });

      if (requestId !== requestIdRef.current) {
        return;
      }

      setContacts(sortedContacts);
    } catch (err) {
      if (requestId !== requestIdRef.current) {
        return;
      }

      console.error('SiteContacts fetch error:', err.message, err.stack);
      setError(`Failed to fetch contacts: ${err.message}`);
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [site]);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  const handleOpenAdd = () => {
    setIsEditing(false);
    const newContact = {
      first_name: '',
      last_name: '',
      country_code: '',
      phone_number: '',
      email: '',
      notify_phone: false,
      notify_email: false,
      site
    };
    setCurrentContact(newContact);
    setOriginalContact({ ...newContact });
    setOpenModal(true);
  };

  const handleOpenEdit = (contact) => {
    setIsEditing(true);
    setCurrentContact({ ...contact });
    setOriginalContact({ ...contact });
    setOpenModal(true);
  };

  const isDirty = () => {
    if (!currentContact || !originalContact) return false;
    return (
      currentContact.first_name !== originalContact.first_name ||
      currentContact.last_name !== originalContact.last_name ||
      currentContact.country_code !== originalContact.country_code ||
      currentContact.phone_number !== originalContact.phone_number ||
      currentContact.email !== originalContact.email ||
      currentContact.notify_phone !== originalContact.notify_phone ||
      currentContact.notify_email !== originalContact.notify_email
    );
  };

  const handleCloseModal = () => {
    if (isDirty()) {
      setOpenConfirmClose(true);
    } else {
      setOpenModal(false);
      setCurrentContact(null);
      setOriginalContact(null);
    }
  };

  const confirmClose = () => {
    setOpenConfirmClose(false);
    setOpenModal(false);
    setCurrentContact(null);
    setOriginalContact(null);
  };

  const cancelClose = () => {
    setOpenConfirmClose(false);
  };

  const handleSave = async () => {
    // Trim all string fields
    const trimmedContact = {
      ...currentContact,
      first_name: currentContact.first_name?.trim() || '',
      last_name: currentContact.last_name?.trim() || '',
      email: currentContact.email?.trim() || '',
      phone_number: currentContact.phone_number?.trim() || '',
      country_code: currentContact.country_code?.trim() || ''
    };

    // Validate: at least email or phone must be provided
    const hasEmail = trimmedContact.email.length > 0;
    const hasPhone = trimmedContact.phone_number.length > 0 && trimmedContact.country_code.length > 0;

    if (!hasEmail && !hasPhone) {
      alert('Please provide at least an email address or phone number');
      return;
    }

    // Validate email format if provided
    if (hasEmail) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(trimmedContact.email)) {
        alert('Please enter a valid email address');
        return;
      }
    }

    // Validate phone if provided
    if (trimmedContact.phone_number.length > 0 || trimmedContact.country_code.length > 0) {
      // Country code must be numeric only (no +)
      if (!/^\d+$/.test(trimmedContact.country_code)) {
        alert('Country code must contain only numbers (no + symbol)');
        return;
      }

      // Phone number must be provided if country code is provided
      if (trimmedContact.country_code.length > 0 && trimmedContact.phone_number.length === 0) {
        alert('Please enter a phone number or clear the country code');
        return;
      }

      // Country code must be provided if phone number is provided
      if (trimmedContact.phone_number.length > 0 && trimmedContact.country_code.length === 0) {
        alert('Please enter a country code (e.g., 1 for US/Canada)');
        return;
      }
    }

    // Validate required fields
    if (!trimmedContact.first_name || !trimmedContact.last_name) {
      alert('First name and last name are required');
      return;
    }

    try {
      if (isEditing) {
        const originalEmail = originalContact?.email || currentContact.email;
        const { error } = await supabase
          .from('site_contacts')
          .update(trimmedContact)
          .eq('site', site)
          .eq('email', originalEmail);

        if (error) {
          throw error;
        }
      } else {
        const { error } = await supabase
          .from('site_contacts')
          .insert(trimmedContact);

        if (error) {
          throw error;
        }
      }

      await fetchContacts();
      setOpenModal(false);
      setCurrentContact(null);
      setOriginalContact(null);
    } catch (err) {
      console.error('Error saving contact:', err);
      alert(`Error: ${err.message}`);
    }
  };

  const handleDelete = () => {
    setOpenConfirmDelete(true);
  };

  const confirmDelete = async () => {
    try {
      const contactEmail = originalContact?.email || currentContact.email;
      const { error } = await supabase
        .from('site_contacts')
        .delete()
        .eq('site', site)
        .eq('email', contactEmail);

      if (error) {
        throw error;
      }

      await fetchContacts();
      setOpenConfirmDelete(false);
      setOpenModal(false);
      setCurrentContact(null);
      setOriginalContact(null);
    } catch (err) {
      console.error('Error deleting contact:', err);
      alert(`Error: ${err.message}`);
    }
  };

  const cancelDelete = () => {
    setOpenConfirmDelete(false);
  };

  const handleExportPDF = async () => {
    const { jsPDF } = await import('jspdf');
    const doc = new jsPDF({ unit: 'mm', format: 'letter' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margins = { left: 10, right: 10, bottom: 10 };
    const colors = {
      ink: [28, 44, 43],
      muted: [92, 108, 107],
      brand: [15, 118, 110],
      brandSoft: [227, 244, 242],
      header: [18, 79, 73],
      border: [193, 213, 210],
      zebra: [247, 250, 249],
      notifyPhone: [234, 245, 255],
      notifyEmail: [255, 245, 231],
    };
    const siteName = site || 'Unknown Site';

    // Load images
    const loadImage = (src) => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = src;
      });
    };

    try {
      const ieImg = await loadImage(IELogo);
      const tpcImg = await loadImage(TPCLogo);
      const now = new Date();
      const zonedDate = toZonedTime(now, timezone);
      const exportDate = format(zonedDate, 'MMMM d, yyyy h:mm a zzz', { timeZone: timezone });

      const drawPageChrome = (pageNum) => {
        doc.setFillColor(...colors.header);
        doc.rect(0, 0, pageWidth, 22, 'F');

        const ieLogoWidth = 26;
        const ieLogoHeight = (ieImg.height / ieImg.width) * ieLogoWidth;
        doc.addImage(ieImg, 'PNG', margins.left, 5, ieLogoWidth, ieLogoHeight);

        const tpcLogoWidth = 24;
        const tpcLogoHeight = (tpcImg.height / tpcImg.width) * tpcLogoWidth;
        doc.addImage(tpcImg, 'PNG', pageWidth - margins.right - tpcLogoWidth, 5, tpcLogoWidth, tpcLogoHeight);

        doc.setTextColor(255, 255, 255);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.text('Field Safety Dashboard', pageWidth / 2, 11.5, { align: 'center' });
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8.5);
        doc.text('Responder Contact Export', pageWidth / 2, 17, { align: 'center' });

        const footerY = pageHeight - 5.5;
        doc.setDrawColor(...colors.border);
        doc.setLineWidth(0.2);
        doc.line(margins.left, pageHeight - 8.5, pageWidth - margins.right, pageHeight - 8.5);
        doc.setTextColor(...colors.muted);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.text(`Exported ${exportDate}`, margins.left, footerY);
        doc.text(`Page ${pageNum}`, pageWidth - margins.right, footerY, { align: 'right' });
      };

      const drawMetaPanel = () => {
        const panelTop = 27;
        const panelHeight = 22;
        doc.setDrawColor(...colors.border);
        doc.setFillColor(...colors.brandSoft);
        doc.roundedRect(
          margins.left,
          panelTop,
          pageWidth - margins.left - margins.right,
          panelHeight,
          2,
          2,
          'FD'
        );

        doc.setTextColor(...colors.brand);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(14);
        doc.text(siteName, margins.left + 4, panelTop + 8);

        doc.setTextColor(...colors.ink);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8.5);
        doc.text(`Timezone: ${timezone || 'UTC'}`, margins.left + 4, panelTop + 13.5);
        doc.text(`Total Contacts: ${contacts.length}`, margins.left + 4, panelTop + 18.5);

        doc.setTextColor(...colors.muted);
        doc.setFontSize(8);
        doc.text('Notification-enabled channels are highlighted.', pageWidth - margins.right - 4, panelTop + 18.5, {
          align: 'right'
        });
      };

      const tableTop = 56;
      const rowLineHeight = 4.3;
      const rowPaddingY = 2.2;
      const headers = ['Name', 'Phone', 'Email', 'Notify'];
      const colWidths = [45, 34, 82, 20];
      const colX = [
        margins.left,
        margins.left + colWidths[0],
        margins.left + colWidths[0] + colWidths[1],
        margins.left + colWidths[0] + colWidths[1] + colWidths[2]
      ];

      const drawTableHeader = (yPos) => {
        doc.setFillColor(...colors.brand);
        doc.roundedRect(margins.left, yPos, pageWidth - margins.left - margins.right, 8, 1.5, 1.5, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        headers.forEach((header, index) => {
          doc.text(header, colX[index] + 2, yPos + 5.3);
        });
        return yPos + 8;
      };

      let pageNum = 1;
      drawPageChrome(pageNum);
      drawMetaPanel();
      let yPos = drawTableHeader(tableTop);

      contacts.forEach((contact, index) => {
        const nameText = `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || 'N/A';
        const phoneText = formatPhoneNumber(contact.country_code, contact.phone_number) || 'N/A';
        const emailText = contact.email || 'N/A';
        const notifyChannels = [
          contact.notify_phone ? 'Phone' : null,
          contact.notify_email ? 'Email' : null
        ].filter(Boolean).join(', ') || 'None';

        const wrappedName = doc.splitTextToSize(nameText, colWidths[0] - 4);
        const wrappedPhone = doc.splitTextToSize(phoneText, colWidths[1] - 4);
        const wrappedEmail = doc.splitTextToSize(emailText, colWidths[2] - 4);
        const wrappedNotify = doc.splitTextToSize(notifyChannels, colWidths[3] - 4);
        const maxLines = Math.max(
          wrappedName.length || 1,
          wrappedPhone.length || 1,
          wrappedEmail.length || 1,
          wrappedNotify.length || 1
        );
        const rowHeight = Math.max(8, (maxLines * rowLineHeight) + (rowPaddingY * 2));

        if (yPos + rowHeight > pageHeight - margins.bottom - 12) {
          doc.addPage();
          pageNum += 1;
          drawPageChrome(pageNum);
          yPos = drawTableHeader(26);
        }

        if (index % 2 === 1) {
          doc.setFillColor(...colors.zebra);
          doc.rect(margins.left, yPos, pageWidth - margins.left - margins.right, rowHeight, 'F');
        }

        doc.setDrawColor(...colors.border);
        doc.setLineWidth(0.15);
        doc.line(margins.left, yPos + rowHeight, pageWidth - margins.right, yPos + rowHeight);
        colX.slice(1).forEach((x) => {
          doc.line(x, yPos, x, yPos + rowHeight);
        });

        if (contact.notify_phone) {
          doc.setFillColor(...colors.notifyPhone);
          doc.roundedRect(colX[1] + 0.9, yPos + 0.9, colWidths[1] - 1.8, rowHeight - 1.8, 1.2, 1.2, 'F');
        }
        if (contact.notify_email) {
          doc.setFillColor(...colors.notifyEmail);
          doc.roundedRect(colX[2] + 0.9, yPos + 0.9, colWidths[2] - 1.8, rowHeight - 1.8, 1.2, 1.2, 'F');
        }

        const getCellStartY = (lineCount) =>
          yPos + ((rowHeight - (lineCount * rowLineHeight)) / 2) + (rowLineHeight * 0.75);
        const drawCellText = (lines, x, startY) => {
          const safeLines = (lines && lines.length > 0) ? lines : [''];
          safeLines.forEach((line, idx) => {
            doc.text(line, x, startY + (idx * rowLineHeight));
          });
        };

        const nameY = getCellStartY(wrappedName.length || 1);
        const phoneY = getCellStartY(wrappedPhone.length || 1);
        const emailY = getCellStartY(wrappedEmail.length || 1);
        const notifyY = getCellStartY(wrappedNotify.length || 1);

        doc.setTextColor(...colors.ink);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8.5);
        drawCellText(wrappedName, colX[0] + 2, nameY);

        doc.setFont('helvetica', contact.notify_phone ? 'bold' : 'normal');
        drawCellText(wrappedPhone, colX[1] + 2, phoneY);

        doc.setFont('helvetica', contact.notify_email ? 'bold' : 'normal');
        drawCellText(wrappedEmail, colX[2] + 2, emailY);

        doc.setFont('helvetica', 'normal');
        drawCellText(wrappedNotify, colX[3] + 2, notifyY);

        yPos += rowHeight;
      });

      doc.setTextColor(...colors.muted);
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(8);
      if (yPos + 6 > pageHeight - margins.bottom - 12) {
        doc.addPage();
        pageNum += 1;
        drawPageChrome(pageNum);
        yPos = 26;
      }
      doc.text('Legend: shaded phone/email cells indicate that alert notifications are enabled for that channel.', margins.left, yPos + 5);

      // Save the PDF
      doc.save(`${siteName}_contacts.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF');
    }
  };

  if (loading) {
    return (
      <Card sx={{ height: { xs: 520, md: 460 } }}>
        <CardContent
          sx={{
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          <CircularProgress sx={{ color: 'var(--color-brand)' }} />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card sx={{ height: { xs: 520, md: 460 } }}>
        <CardContent sx={{ p: 2 }}>
          <Typography color="error">{error}</Typography>
          <Button
            variant="outlined"
            onClick={fetchContacts}
            sx={{ mt: 2, color: 'var(--color-brand)', borderColor: 'var(--color-brand)' }}
          >
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card sx={{ height: { xs: 520, md: 460 }, overflow: 'hidden' }}>
        <CardContent
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            p: 0
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              p: 2,
              pb: 1.4,
              justifyContent: 'space-between',
              borderBottom: '1px solid rgba(17, 47, 38, 0.12)',
              background:
                'linear-gradient(120deg, rgba(31, 92, 74, 0.1), rgba(255, 255, 255, 0.92) 55%, rgba(227, 83, 2, 0.08))',
            }}
          >
            <Typography
              variant="h6"
              sx={{ flexShrink: 0, color: 'var(--color-brand-dark)' }}
            >
              Site Contacts
            </Typography>

            <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 1 }}>
              {isAdmin && (
                <Button
                  variant="contained"
                  onClick={handleOpenAdd}
                  sx={{
                    bgcolor: 'var(--color-brand)',
                    boxShadow: 'none',
                    '&:hover': {
                      bgcolor: 'var(--color-brand-dark)',
                      boxShadow: 'none',
                    }
                  }}
                >
                  Add Contact
                </Button>
              )}

              <Button
                variant="outlined"
                onClick={handleExportPDF}
                startIcon={<DownloadIcon />}
                sx={{
                  color: 'var(--color-brand)',
                  borderColor: 'rgba(31, 92, 74, 0.45)',
                  '&:hover': {
                    borderColor: 'var(--color-brand)',
                    backgroundColor: 'rgba(31, 92, 74, 0.08)'
                  }
                }}
              >
                Export PDF
              </Button>

              <Tooltip
                arrow
                title={
                  <Box sx={{ fontSize: '14px', lineHeight: 1.5 }}>
                    Bold phone/email indicates notifications are enabled.
                  </Box>
                }
              >
                <IconButton sx={{ color: 'var(--color-brand)' }}>
                  <InfoIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <Box sx={{ flexGrow: 1, minHeight: 0, p: 1.5 }}>
            <TableContainer
              component={Paper}
              sx={{
                height: '100%',
                overflowY: 'auto',
                overflowX: 'auto',
                border: '1px solid var(--color-border)',
                boxShadow: 'none',
                scrollbarWidth: 'thin',
                scrollbarColor: 'rgba(15, 92, 81, 0.45) rgba(15, 92, 81, 0.14)',
                '&::-webkit-scrollbar': {
                  width: '10px',
                  height: '10px',
                },
                '&::-webkit-scrollbar-track': {
                  background: 'rgba(15, 92, 81, 0.14)',
                  borderRadius: '9px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: 'rgba(15, 92, 81, 0.45)',
                  borderRadius: '9px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  background: 'rgba(15, 92, 81, 0.62)',
                },
              }}
            >
              <Table stickyHeader sx={{ minWidth: 520 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e4f0ec', whiteSpace: 'nowrap', py: 1.5, '&.MuiTableCell-stickyHeader': { backgroundColor: '#e4f0ec', zIndex: 4 } }}>
                      Name
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e4f0ec', whiteSpace: 'nowrap', py: 1.5, '&.MuiTableCell-stickyHeader': { backgroundColor: '#e4f0ec', zIndex: 4 } }}>
                      Phone
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e4f0ec', whiteSpace: 'nowrap', py: 1.5, '&.MuiTableCell-stickyHeader': { backgroundColor: '#e4f0ec', zIndex: 4 } }}>
                      Email
                    </TableCell>
                    <TableCell
                      sx={{ fontWeight: 'bold', backgroundColor: '#e4f0ec', py: 1.5, '&.MuiTableCell-stickyHeader': { backgroundColor: '#e4f0ec', zIndex: 4 } }}
                      align="right"
                    >
                      Edit
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {contacts.map((contact, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        backgroundColor: '#fff',
                        '&:hover': {
                          backgroundColor: 'rgba(31, 92, 74, 0.08)'
                        },
                        '&:nth-of-type(odd)': {
                          backgroundColor: 'rgba(248, 251, 250, 0.95)',
                        },
                      }}
                    >
                      <TableCell sx={{ py: 1.5, whiteSpace: 'normal', lineHeight: 1.2 }}>
                        <strong>{contact.first_name}</strong>
                        <br />
                        {contact.last_name}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: contact.notify_phone ? 'bold' : 'normal',
                          color: contact.notify_phone ? '#000' : '#757575',
                          whiteSpace: 'nowrap',
                          py: 1.5
                        }}
                      >
                        {formatPhoneNumber(contact.country_code, contact.phone_number)}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: contact.notify_email ? 'bold' : 'normal',
                          color: contact.notify_email ? '#000' : '#757575',
                          whiteSpace: 'nowrap',
                          py: 1.5
                        }}
                      >
                        {contact.email}
                      </TableCell>
                      <TableCell align="right" sx={{ py: 1.5 }}>
                        {canEditContact(contact) && (
                          <IconButton
                            onClick={() => handleOpenEdit(contact)}
                            size="small"
                            sx={{ color: 'var(--color-brand)' }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </CardContent>
      </Card>

      <Dialog open={openModal} onClose={handleCloseModal} fullWidth>
        <Box
          sx={{
            background: 'linear-gradient(120deg, var(--color-brand-dark), var(--color-brand))',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1
          }}
        >
          <Typography variant="h6">
            {isEditing ? 'Edit Contact' : 'Add Contact'}
          </Typography>
          <IconButton onClick={handleCloseModal} sx={{ color: '#fff' }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent>
          <Box
            component="form"
            sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}
          >
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="First Name"
                value={currentContact?.first_name || ''}
                onChange={(e) =>
                  setCurrentContact({ ...currentContact, first_name: e.target.value })
                }
                fullWidth
              />
              <TextField
                label="Last Name"
                value={currentContact?.last_name || ''}
                onChange={(e) =>
                  setCurrentContact({ ...currentContact, last_name: e.target.value })
                }
                fullWidth
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Country Code"
                value={currentContact?.country_code || ''}
                onChange={(e) => {
                  // Only allow numeric input (no + symbol)
                  const value = e.target.value.replace(/\D/g, '');
                  setCurrentContact({ ...currentContact, country_code: value });
                }}
                placeholder="1"
                helperText="Numbers only (no + symbol)"
                sx={{ width: '30%' }}
              />
              <TextField
                label="Phone Number"
                value={currentContact?.phone_number || ''}
                onChange={(e) =>
                  setCurrentContact({ ...currentContact, phone_number: e.target.value })
                }
                sx={{ width: '70%' }}
              />
            </Box>
            <TextField
              label="Email"
              value={currentContact?.email || ''}
              onChange={(e) =>
                setCurrentContact({ ...currentContact, email: e.target.value })
              }
              fullWidth
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={currentContact?.notify_phone || false}
                    onChange={(e) =>
                      setCurrentContact({
                        ...currentContact,
                        notify_phone: e.target.checked
                      })
                    }
                    sx={{
                      color: 'var(--color-brand)',
                      '&.Mui-checked': { color: 'var(--color-brand)' }
                    }}
                  />
                }
                label="Notify by Phone"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={currentContact?.notify_email || false}
                    onChange={(e) =>
                      setCurrentContact({
                        ...currentContact,
                        notify_email: e.target.checked
                      })
                    }
                    sx={{
                      color: 'var(--color-brand)',
                      '&.Mui-checked': { color: 'var(--color-brand)' }
                    }}
                  />
                }
                label="Notify by Email"
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'flex-end' }}>
          {isEditing && isAdmin && (
            <IconButton onClick={handleDelete} sx={{ color: '#e74c3c' }}>
              <DeleteIcon />
            </IconButton>
          )}
          <IconButton onClick={handleSave} sx={{ color: '#2ecc71' }}>
            <SaveIcon />
          </IconButton>
        </DialogActions>
      </Dialog>

      <Dialog open={openConfirmDelete} onClose={cancelDelete}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {currentContact?.first_name}{' '}
            {currentContact?.last_name}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDelete} sx={{ color: 'var(--color-brand)' }}>
            No
          </Button>
          <Button onClick={confirmDelete} variant="contained" color="error">
            Yes, Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openConfirmClose} onClose={cancelClose}>
        <DialogTitle>Unsaved Changes</DialogTitle>
        <DialogContent>
          <Typography>
            You have unsaved changes. Are you sure you want to close without saving?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelClose} sx={{ color: 'var(--color-brand)' }}>
            No, Keep Editing
          </Button>
          <Button onClick={confirmClose} variant="contained" color="warning">
            Yes, Discard Changes
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SiteContacts;
